import { normalizeInventoryComponent } from './inventoryPouchContextModel.js';

export const INVENTORY_BARCODE_SET_ID = 'INVENTORY-BARCODE';

const GLOBAL_COMPONENTS = Symbol('inventory-barcode-components');
const POUCH_CONTEXT_MAX_AGE_MS = 5 * 60 * 1000;
const POUCH_CONTEXT_MIN_CONFIDENCE = 2;

let recentComponent = '';
let recentComponentConfidence = 0;
let recentComponentAt = 0;

function normalizeKey(value) {
  return String(value || '').trim();
}

function safeToken(value) {
  const text = normalizeKey(value);
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const slug = text.replace(/[^A-Za-z0-9_.:-]+/g, '-').slice(0, 80) || 'BARCODE';
  return `${slug}-${(hash >>> 0).toString(16).toUpperCase().padStart(8, '0')}`;
}

function allowedSet(value) {
  if (!value) return null;
  if (value instanceof Set) return value;
  return new Set(Array.isArray(value) ? value : [value]);
}

function componentForCatalogId(globalMap, catalogId) {
  return String(globalMap?.[GLOBAL_COMPONENTS]?.[catalogId] || '').trim();
}

function contextFresh(now = Date.now()) {
  return Boolean(recentComponent && recentComponentAt && now - recentComponentAt <= POUCH_CONTEXT_MAX_AGE_MS);
}

function rememberResolvedComponent(globalMap, catalogId, explicit = false, now = Date.now()) {
  const component = componentForCatalogId(globalMap, catalogId);
  if (!component) return;
  const normalized = normalizeInventoryComponent(component);
  if (!normalized) return;

  if (normalizeInventoryComponent(recentComponent) === normalized && contextFresh(now)) {
    recentComponentConfidence += 1;
  } else {
    recentComponent = component;
    recentComponentConfidence = explicit ? POUCH_CONTEXT_MIN_CONFIDENCE : 1;
  }
  if (explicit) recentComponentConfidence = Math.max(recentComponentConfidence, POUCH_CONTEXT_MIN_CONFIDENCE);
  recentComponentAt = now;
}

function contextualCandidate(candidates, globalMap, now = Date.now()) {
  if (!contextFresh(now) || recentComponentConfidence < POUCH_CONTEXT_MIN_CONFIDENCE) return '';
  const normalized = normalizeInventoryComponent(recentComponent);
  const matches = candidates.filter(id => normalizeInventoryComponent(componentForCatalogId(globalMap, id)) === normalized);
  return matches.length === 1 ? matches[0] : '';
}

export function resetInventoryBarcodeResolutionContext() {
  recentComponent = '';
  recentComponentConfidence = 0;
  recentComponentAt = 0;
}

export function inventoryBarcodeResolutionContext(now = Date.now()) {
  if (!contextFresh(now)) return { component: '', confidence: 0 };
  return { component: recentComponent, confidence: recentComponentConfidence };
}

export function isInventoryBarcodeRecord(record) {
  return String(record?.setId || '').trim().toUpperCase() === INVENTORY_BARCODE_SET_ID;
}

export function getGlobalBarcodeMap(expirations = []) {
  const map = {};
  const components = {};
  Object.defineProperty(map, GLOBAL_COMPONENTS, { value: components, enumerable: false });

  (Array.isArray(expirations) ? expirations : []).forEach(record => {
    if (!isInventoryBarcodeRecord(record)) return;
    const key = normalizeKey(record?.lotSerial);
    const catalogId = String(record?.catalogId || '').trim();
    if (!key || !catalogId) return;
    if (!map[key]) map[key] = [];
    if (!map[key].includes(catalogId)) map[key].push(catalogId);
    const component = String(record?.component || '').trim();
    if (component) components[catalogId] = component;
  });
  return map;
}

export function barcodeMappedCatalogIds(lookupKeys, globalMap = {}) {
  const ids = [];
  for (const key of Array.isArray(lookupKeys) ? lookupKeys : []) {
    const value = globalMap?.[key];
    const candidates = Array.isArray(value) ? value : (value ? [value] : []);
    candidates.forEach(id => {
      if (id && !ids.includes(id)) ids.push(id);
    });
  }
  return ids;
}

export function resolveBarcodeCatalogId(lookupKeys, globalMap = {}, localMap = {}, allowedCatalogIds = null, preferredCatalogId = '') {
  const allowed = allowedSet(allowedCatalogIds);
  const candidates = barcodeMappedCatalogIds(lookupKeys, globalMap)
    .filter(id => !allowed || allowed.has(id));
  const preferred = String(preferredCatalogId || '').trim();

  if (preferred && candidates.includes(preferred)) {
    rememberResolvedComponent(globalMap, preferred, true);
    return preferred;
  }
  if (preferred && candidates.length > 0) return '';

  if (candidates.length === 1) {
    rememberResolvedComponent(globalMap, candidates[0]);
    return candidates[0];
  }

  if (candidates.length > 1) {
    const contextual = contextualCandidate(candidates, globalMap);
    if (contextual) {
      rememberResolvedComponent(globalMap, contextual);
      return contextual;
    }
    return '';
  }

  for (const key of Array.isArray(lookupKeys) ? lookupKeys : []) {
    const id = localMap?.[key];
    if (id && (!allowed || allowed.has(id))) {
      rememberResolvedComponent(globalMap, id);
      return id;
    }
  }
  return '';
}

export function makeInventoryBarcodeMappingRecord(catalogItem, lookupKey) {
  const key = normalizeKey(lookupKey);
  const catalogId = String(catalogItem?.catalogId || '').trim();
  if (!key) throw new Error('Barcode lookup key is required.');
  if (!catalogId) throw new Error('Catalog ID is required to map a barcode.');
  return {
    recordId: `${INVENTORY_BARCODE_SET_ID}-${safeToken(key)}-${safeToken(catalogId)}`,
    setId: INVENTORY_BARCODE_SET_ID,
    setName: 'Inventory Barcode Library',
    catalogId,
    component: String(catalogItem?.component || '').trim(),
    item: String(catalogItem?.item || '').trim(),
    requiredQty: '0',
    expirationDate: '',
    expirationState: 'NA',
    lotSerial: key,
    returnedToLogistics: true,
    returnedDate: '',
    replacementDate: '',
    notes: 'Global barcode mapping',
    updatedAt: '',
  };
}
